import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMutation } from 'react-apollo';
import { Formik } from 'formik';

import ImageUpload from '../../../../../../../components/global/ImageUpload';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { cardCategoryUpdate as cardCategoryUpdateMutation } from '../../../../../graphql/mutations/cardCategory';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import Button from '../../../../../../../components/global/Button';
import FormGroup from '../../../../../../../components/global/FormGroup';

const propTypes = {
  className: PropTypes.string,
  cardCategory: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    asset_image: PropTypes.shape({
      id: PropTypes.string,
      url: PropTypes.string.isRequired,
    }),
    preview_asset_image: PropTypes.shape({
      id: PropTypes.string,
      url: PropTypes.string.isRequired,
    }),
    description: PropTypes.string,
    background_color: PropTypes.string,
    foreground_color: PropTypes.string,
    push_notification_type: PropTypes.string,
  }).isRequired,
};

const CardCategoryEdit = ({ className, cardCategory }) => {
  const [cardCategoryUpdate] = useMutation(cardCategoryUpdateMutation);
  const [loading, setLoading] = useState(false);

  const onSubmit = async input => {
    setLoading(true);
    await cardCategoryUpdate({
      variables: {
        input: {
          card_category_id: cardCategory.id,
          ...input,
        },
      },
    });
    setLoading(false);
  };

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      onSubmit({ asset_image_id: assetImage.id });
    },
  });

  const {
    uploading: uploadingPreview,
    onUpload: onPreviewUpload,
    onUploadReset: onPreviewUploadReset,
  } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      onSubmit({ preview_asset_image_id: assetImage.id });
    },
  });

  const classes = classNames({
    [className]: !!className,
  });

  return (
    <div className={classes}>
      <h5>{cardCategory.name}</h5>

      <Formik
        initialValues={{
          background_color: cardCategory.background_color ?? '',
          foreground_color: cardCategory.foreground_color ?? '',
          description: cardCategory.description ?? '',
          push_notification_type:
            cardCategory.push_notification_type ?? undefined,
        }}
        onSubmit={onSubmit}
        render={({ values, handleSubmit, handleChange }) => {
          return (
            <form onSubmit={handleSubmit}>
              <ImageUpload
                id={`${cardCategory.id}-image-upload`}
                loading={uploading}
                disabled={uploading || loading}
                onImageDismiss={() => {
                  onUploadReset();
                  onSubmit({ asset_image_id: null });
                }}
                onInputChange={event => {
                  const [file] = event.target.files;
                  onUpload(file);
                }}
                image={
                  cardCategory?.asset_image
                    ? {
                        id: cardCategory?.asset_image?.id,
                        thumbnail_urls: [
                          {
                            size_code: IMAGE_SIZE_CODE.LARGE,
                            url: cardCategory?.asset_image?.url,
                            width: 1,
                            height: 1,
                          },
                        ],
                      }
                    : undefined
                }
                className="mb-2"
                notes={['The main Category photo.']}
              />

              <ImageUpload
                id={`${cardCategory.id}-preview-image-upload`}
                loading={uploadingPreview}
                disabled={uploadingPreview || loading}
                onImageDismiss={() => {
                  onPreviewUploadReset();
                  onSubmit({ preview_asset_image_id: null });
                }}
                onInputChange={event => {
                  const [file] = event.target.files;
                  onPreviewUpload(file);
                }}
                image={
                  cardCategory?.preview_asset_image
                    ? {
                        id: cardCategory?.preview_asset_image?.id,
                        thumbnail_urls: [
                          {
                            size_code: IMAGE_SIZE_CODE.LARGE,
                            url: cardCategory?.preview_asset_image?.url,
                            width: 1,
                            height: 1,
                          },
                        ],
                      }
                    : undefined
                }
                className="mb-2"
                notes={['The interstitial preview image.']}
              />

              <FormGroup
                id="description"
                label="Description"
                name="description"
                size="sm"
                onChange={handleChange}
                value={values.description}
                disabled={loading}
              />

              <FormGroup
                id="background-color"
                label="Background color"
                name="background_color"
                size="sm"
                onChange={handleChange}
                value={values.background_color}
                disabled={loading}
              />

              <FormGroup
                id="foreground-color"
                label="Foreground color"
                name="foreground_color"
                size="sm"
                onChange={handleChange}
                value={values.foreground_color}
                disabled={loading}
              />

              <FormGroup
                id="push-notification-type"
                label="Push notification type"
                name="push_notification_type"
                size="sm"
                onChange={handleChange}
                value={values.push_notification_type}
                disabled={loading}
              />

              <Button btnSize="xs" type="submit">
                {loading ? 'Loading...' : 'Save'}
              </Button>
            </form>
          );
        }}
      />
    </div>
  );
};

CardCategoryEdit.propTypes = propTypes;

export default CardCategoryEdit;
